import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const MathChallengeModal = ({ isOpen, onClose, onSuccess, action = 'perform this action' }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [questionType, setQuestionType] = useState(0); // 0: +, 1: -, 2: ×
  const [answer, setAnswer] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (isOpen) {
      generateQuestion();
      setAnswer('');
      setAttempts(0);
    }
  }, [isOpen]);

  const generateQuestion = () => {
    const questionType = Math.floor(Math.random() * 3);
    let n1, n2;

    if (questionType === 0) {
      n1 = Math.floor(Math.random() * 50) + 20;
      n2 = Math.floor(Math.random() * 50) + 20;
    } else if (questionType === 1) {
      n1 = Math.floor(Math.random() * 50) + 50;
      n2 = Math.floor(Math.random() * 40) + 10;
    } else {
      n1 = Math.floor(Math.random() * 9) + 2;
      n2 = Math.floor(Math.random() * 9) + 2;
    }

    setNum1(n1);
    setNum2(n2);
    setQuestionType(questionType);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let correctAnswer;
    if (questionType === 0) correctAnswer = num1 + num2;
    else if (questionType === 1) correctAnswer = num1 - num2;
    else correctAnswer = num1 * num2;

    const userAnswer = parseInt(answer);

    if (userAnswer === correctAnswer) {
      toast.success('Correct! Access granted.');
      onSuccess();
      onClose();
    } else {
      setAttempts(attempts + 1);
      setShake(true);
      setTimeout(() => setShake(false), 400);

      if (attempts >= 2) {
        toast.error('Too many incorrect attempts. Please ask a parent for help.');
        onClose();
      } else {
        toast.error('Incorrect. Try again.');
        setAnswer('');
        generateQuestion();
      }
    }
  };

  if (!isOpen) return null;

  const op = questionType === 0 ? '+' : questionType === 1 ? '−' : '×';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        background: "rgba(11,26,51,0.42)",
        backdropFilter: "blur(8px)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      <div
        className="relative w-full max-w-md p-6 overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          border: "2px solid rgba(11,26,51,0.06)",
          borderRadius: 28,
          boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
          animation: shake ? "mc-shake 0.4s ease-in-out" : undefined,
        }}
      >
        {/* Decorative blob */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -70,
            right: -70,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(0,101,248,0.10)",
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />

        <div className="relative">
          {/* Header */}
          <div className="flex items-start gap-3 mb-1">
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                color: "#FFFFFF",
                boxShadow: "0px 3px 0px #073B8E",
              }}
            >
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <div>
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                Parent Check
              </span>
              <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 0", lineHeight: 1.05 }}>
                Quick math check
              </h2>
            </div>
          </div>

          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "12px 0 18px" }}>
            To {action}, solve this math problem.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Math question card */}
            <div
              className="text-center mb-4 overflow-hidden"
              style={{
                background: "#FFFFFF",
                border: "2px solid rgba(0,101,248,0.18)",
                borderRadius: 22,
                padding: "24px 20px",
                boxShadow: "0px 3px 0px rgba(11,26,51,0.05), 0px 12px 28px rgba(11,26,51,0.06)",
              }}
            >
              <div
                className="flex items-center justify-center gap-3 mb-4"
                style={{ ...FREDOKA, fontWeight: 700, fontSize: 44, letterSpacing: "-0.02em", lineHeight: 1 }}
              >
                <span style={{ color: "#0B1A33" }}>{num1}</span>
                <span style={{ color: "#0065F8" }}>{op}</span>
                <span style={{ color: "#0B1A33" }}>{num2}</span>
                <span style={{ color: "#94A3B8" }}>=</span>
                <span style={{ color: "#0065F8" }}>?</span>
              </div>
              <input
                type="number"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Your answer"
                autoFocus
                style={{
                  ...FREDOKA,
                  width: "100%",
                  background: "#F5FAFF",
                  color: "#0B1A33",
                  fontSize: 24,
                  fontWeight: 700,
                  textAlign: "center",
                  border: "2px solid rgba(0,101,248,0.22)",
                  borderRadius: 14,
                  padding: "12px 16px",
                  outline: "none",
                  transition: "border-color 200ms, box-shadow 200ms",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.15)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(0,101,248,0.22)"; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>

            {/* Attempts indicator */}
            <div className="flex items-center justify-center gap-2 mb-5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 24,
                    height: 6,
                    borderRadius: 999,
                    background: i < attempts ? "#DC2626" : i === attempts ? "#0065F8" : "rgba(11,26,51,0.12)",
                    transition: "background 200ms",
                  }}
                />
              ))}
              <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, fontWeight: 600, marginLeft: 6 }}>
                Attempt {attempts + 1} of 3
              </span>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#5A6B85",
                  fontWeight: 700,
                  fontSize: 14,
                  border: "2px solid rgba(11,26,51,0.10)",
                  borderRadius: 999,
                  padding: "12px 18px",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!answer}
                className="flex-1 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  ...FREDOKA,
                  background: "#0065F8",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 14,
                  border: "none",
                  borderRadius: 999,
                  padding: "12px 18px",
                  boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                  cursor: answer ? "pointer" : "not-allowed",
                }}
              >
                Submit
              </button>
            </div>
          </form>

          {/* Footer */}
          <p
            className="text-center mt-5 inline-flex items-center justify-center gap-1.5 w-full"
            style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontWeight: 600 }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Protects kid profiles from accidental actions
          </p>
        </div>

        <style>{`
          @keyframes mc-shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-5px); }
            80% { transform: translateX(5px); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MathChallengeModal;
